import axios from 'axios';
import { API_URL } from '../../config/api';
import { languageLabel, normalizeLanguage, utcTime } from '../users/model';
export interface Device {
  id: number; user_id: number; user_username: string | null; user_name: string | null;
  user_avatar: string | null; platform: string | null; app_version: string | null;
  os_version: string | null; device_model: string | null; language: string | null;
  is_active: boolean; last_used: string | null; created_at: string | null;
}
export interface DeviceStats {
  total_devices: number; active_devices: number;
  by_platform: { platform: string | null; count: number }[];
  by_os_version: { version: string | null; count: number }[];
  by_device_model: { model: string | null; count: number }[];
  by_language: { language: string | null; count: number }[];
}
export interface Filters { active: '' | 'active' | 'inactive'; platform: string; language: string; version: string }
export const defaults: Filters = { active: '', platform: '', language: '', version: '' };
export const nameOf = (item: Device) => item.user_name || (item.user_username !== 'Unknown' && item.user_username) || `Kullanıcı #${item.user_id}`;
export const usernameOf = (item: Device) => item.user_username && item.user_username !== 'Unknown' ? `@${item.user_username}` : 'Kullanıcı adı yok';
export const platformKey = (raw: string | null) => raw?.toLowerCase() || 'unknown';
export const platformLabel = (raw: string | null) => platformKey(raw) === 'ios' ? 'iOS' : platformKey(raw) === 'android' ? 'Android' : raw || 'Belirtilmemiş';
export const deviceTone = (raw: string | null) => ['ios', 'android'].includes(platformKey(raw)) ? platformKey(raw) : 'other';
export function matches(item: Device, search: string, filters: Filters) {
  const text = [item.id, item.user_id, item.user_username, item.user_name, item.device_model, item.platform, item.app_version, item.os_version, languageLabel(item.language)].join(' ').toLocaleLowerCase('tr-TR');
  return text.includes(search.trim().toLocaleLowerCase('tr-TR')) && (!filters.active || item.is_active === (filters.active === 'active')) &&
    (!filters.platform || platformKey(item.platform) === filters.platform) && (!filters.language || normalizeLanguage(item.language) === filters.language) &&
    (!filters.version || (item.app_version || 'unknown') === filters.version);
}
export type Sort = 'used' | 'created' | 'name';
export function sortItems(items: Device[], sort: Sort) {
  const time = (item: Device) => { const raw = sort === 'created' ? item.created_at : item.last_used; const value = raw ? utcTime(raw) : NaN; return Number.isFinite(value) ? value : -Infinity; };
  return [...items].sort((a, b) => sort === 'name' ? nameOf(a).localeCompare(nameOf(b), 'tr-TR') || a.id - b.id : (time(a) === time(b) ? b.id - a.id : time(a) > time(b) ? -1 : 1));
}
export async function fetchDevices() {
  const items: Device[] = [], seen = new Set<number>(); let skip = 0, total = 0;
  while (skip < 100000) {
    const { data } = await axios.get<{total: number; devices: Device[]}>(`${API_URL}/admin/devices`, { params: { skip, limit: 1000 } });
    if (!Array.isArray(data?.devices) || !Number.isInteger(data.total) || data.total < 0) throw new Error('Invalid device list');
    total = data.total;
    if (!data.devices.length && skip < total) throw new Error('Incomplete device list');
    for (const item of data.devices) if (!seen.has(item.id)) { items.push(item); seen.add(item.id); }
    skip += data.devices.length;
    if (skip >= total || !data.devices.length) break;
  }
  return { items, total, partial: items.length < total };
}
export function requestError(error: unknown) {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (status === 401 || status === 403) return 'Bu işlem için yetkiniz yok veya oturumunuz sona erdi.';
    if (status === 429) return 'Çok fazla istek gönderildi. Biraz sonra yeniden deneyin.';
    if (status === 404) return 'Cihaz kaydı artık bulunmuyor. Listeyi yenileyin.';
  }
  return 'İşlem tamamlanamadı. Yeniden deneyebilirsiniz.';
}
